import React, { useState, useRef, useEffect, useContext } from "react";
import {
  ChevronDown,
  FileText,
  Download,
  Calculator,
  FlaskConical,
} from "lucide-react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import GoalQuestions from "./GoalQuestions_new";
import { useQuiz } from "../contexts/QuizContext";
import { UserContext } from "../contexts/UserContext";
import { toast } from "react-toastify";
import "../styles/components/SamplePaper.css";
import axios from "axios";

const QUESTION_PROMPT =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore";

const MCQ_OPTIONS = [
  { value: "A", label: "A. Lorem ipsum" },
  { value: "B", label: "B. Dolor sit amet" },
  { value: "C", label: "C. Consectetur adipiscing elit" },
  { value: "D", label: "D. Sed do eiusmod tempor incididunt ut labore" },
];

const normalizeQuestionType = (type) => {
  if (!type) return "mcq";
  const normalized =
    typeof type === "string" ? type.trim().toLowerCase() : "mcq";
  switch (normalized) {
    case "short answer":
      return "short_answer";
    case "true/false":
    case "true false":
      return "true_false";
    case "mcq":
      return "mcq";
    default:
      return normalized.replace(/[^a-z0-9]+/g, "_") || "mcq";
  }
};

const buildPlaceholderQuestions = (count) => {
  const safeCount = Number.isFinite(Number(count))
    ? Math.max(Number(count), 1)
    : 1;
  return Array.from({ length: safeCount }, (_, i) => ({
    questionText: `Sample Question ${i + 1}: ${QUESTION_PROMPT}`,
    options: MCQ_OPTIONS.map((o) => o.label.replace(/^\w\.\s*/, "")),
    correctAnswer: 1,
    explanation: "This is a placeholder explanation for the answer.",
  }));
};

/**
 * Custom Dropdown Component for Subject/Topic Selection
 * Mimics the look of the attached image selection boxes.
 * * @param {object} props - Component props
 * @param {string} props.label - The label for the dropdown.
 * @param {string} props.iconName - The name of the icon (e.g., 'Mathematics', 'Algebra').
 * @param {string} props.selectedValue - The currently selected value.
 * @param {function} props.onChange - Handler for value change.
 * @param {Array<string>} props.options - Array of available options.
 * @param {boolean} props.disabled - Whether the dropdown is disabled.
 */
const SelectionDropdown = ({
  label,
  iconName,
  selectedValue,
  onChange,
  options,
  disabled = false,
}) => {
  const isSelected = selectedValue && selectedValue !== "default";

  // NOTE: For a real app, you'd use a proper icon library or local SVGs.
  // Using a simple FileText icon here for demonstration.
  const Icon = FileText;

  return (
    <div className="flex flex-col">
      <label className="block mb-2 text-gray-800 font-semibold text-sm">
        {label}
      </label>
      <div
        className={`relative flex items-center bg-white p-3 rounded-xl transition duration-300 ease-in-out
          ${
            disabled
              ? "border border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
              : isSelected
              ? "border-purple-600 ring-2 ring-purple-200 shadow-lg cursor-pointer"
              : "border border-gray-300 hover:border-purple-400 cursor-pointer"
          }`}
      >
        <div className="flex items-center flex-grow">
          {/* Custom icon area - using a placeholder for the file icon style */}
          <div className="bg-white p-1 rounded mr-3">
            <Icon className="w-5 h-5 text-red-500" />
          </div>

          <select
            value={selectedValue}
            onChange={onChange}
            disabled={disabled}
            className="appearance-none bg-transparent border-none outline-none text-base text-gray-700 font-medium flex-grow pr-8 cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400"
          >
            <option value="default" disabled className="text-gray-400">
              {label.replace("Select a ", "Choose ")}
            </option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Custom Chevron Icon for Dropdown visual */}
        <ChevronDown className="absolute right-3 w-5 h-5 text-gray-500 pointer-events-none" />
      </div>
    </div>
  );
};

/**
 * Main component for the Exam Preparation Test Yourself UI.
 */
function MathematicsSamplePaper() {
  // State for user selections
  const [selectedSubject, setSelectedSubject] = useState("default");
  const [selectedTopic, setSelectedTopic] = useState("default");
  const [numQuestions, setNumQuestions] = useState(5); // Pre-select 5 based on image
  const [questionType, setQuestionType] = useState("MCQ"); // Pre-select MCQ based on image
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const timerRef = useRef(null);
  const { saveQuizState, resetQuizState } = useQuiz();
  const navigate = useNavigate();
  const location = useLocation();
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const userContext = useContext(UserContext);
  const { userData } = userContext || {};
  const { board, grade } = userData || {};

  // State for syllabus data
  const [syllabusData, setSyllabusData] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [isSyllabusLoading, setIsSyllabusLoading] = useState(false);

  // Check if we're on the nested generated route
  const isOnGeneratedRoute = location.pathname.endsWith("/generated");

  // Fetch syllabus data on component mount
  useEffect(() => {
    const fetchSyllabus = async () => {
      setIsSyllabusLoading(true);
      try {
        const response = await axios.get(`${baseUrl}/api/v2/syllabus`, {
          params: {
            board,
            grade,
          },
        });
        const data = response.data || [];
        setSyllabusData(data);
        // Extract unique subjects
        const uniqueSubjects = [...new Set(data.map((item) => item.subject))];
        setSubjectOptions(uniqueSubjects);
      } catch (error) {
        console.error("Error fetching syllabus:", error);
        toast.error("Failed to load syllabus data");
        setSyllabusData([]);
        setSubjectOptions([]);
      } finally {
        setIsSyllabusLoading(false);
      }
    };

    fetchSyllabus();
  }, [baseUrl, board, grade]);

  // Handle subject change
  const handleSubjectChange = (selectedSubject) => {
    setSelectedSubject(selectedSubject);
    // Find chapters for the selected subject
    const subjectData = syllabusData.find(
      (item) => item.subject === selectedSubject
    );
    if (subjectData && subjectData.chapters) {
      // Extract chapter names from the chapters array
      const chapterNames = subjectData.chapters.map((chapter) => chapter.name);
      setChapters(chapterNames);
      // Reset selected topic when subject changes
      setSelectedTopic("default");
    } else {
      setChapters([]);
      setSelectedTopic("default");
    }
  };

  // Dynamic topic options based on selected subject
  const topicOptions = chapters;
  const questionCountOptions = [5, 10, 15, 20, 25];
  const questionTypeOptions = ["MCQ", "True/False", "Short Answer"];

  // Handler for the main button click
  const openQuizModal = ({
    quizPayload,
    totalQuestions,
    timer,
    goalDetails,
  }) => {
    const questionList = Array.isArray(quizPayload?.questions)
      ? quizPayload.questions
      : Array.isArray(quizPayload)
      ? quizPayload
      : [];
    if (!questionList.length) {
      toast.error("No questions available to start the quiz.");
      return;
    }
    const safeTotal =
      Number.isFinite(Number(totalQuestions)) && Number(totalQuestions) > 0
        ? Number(totalQuestions)
        : typeof quizPayload?.question_count === "number" &&
          Number.isFinite(quizPayload.question_count)
        ? Math.max(quizPayload.question_count, questionList.length)
        : questionList.length || 1;
    const computedTimer =
      Number.isFinite(Number(timer)) && Number(timer) > 0
        ? Number(timer)
        : typeof quizPayload?.timer === "number" &&
          Number.isFinite(quizPayload.timer) &&
          quizPayload.timer > 0
        ? quizPayload.timer
        : safeTotal * 60;
    const userAnswersSeed = Array.from({ length: safeTotal }, () => null);

    const storedQuestions = Array.isArray(quizPayload)
      ? {
          questions: questionList,
          question_count: safeTotal,
          timer: computedTimer,
          subject: goalDetails?.subject ?? selectedSubject,
          topic: goalDetails?.topic ?? selectedTopic,
        }
      : quizPayload;

    saveQuizState({
      topicId: "sample-paper",
      questions: storedQuestions,
      goalDetails: goalDetails || {
        subject: selectedSubject,
        topic: selectedTopic,
      },
      currentQuestionIndex: 0,
      selectedOption: null,
      score: 0,
      showExplanation: false,
      quizCompleted: false,
      leveledUp: false,
      goalStatus: "",
      userAnswers: userAnswersSeed,
    });

    setSelectedQuestions(safeTotal);
    setSelectedAnswers(userAnswersSeed);
    setCurrentQuestionIndex(0);
    setRemainingTime(computedTimer);
    setIsModalOpen(true);
  };
  const handleGenerate = async () => {
    if (
      !selectedSubject ||
      selectedSubject === "default" ||
      !selectedTopic ||
      selectedTopic === "default"
    ) {
      toast.error("Please select both subject and topic before generating.");
      return;
    }
    if (!questionType) {
      toast.error("Please select a question type.");
      return;
    }
    const totalRequested = Number(numQuestions);
    if (!Number.isFinite(totalRequested) || totalRequested <= 0) {
      toast.error("Please choose a valid number of questions.");
      return;
    }
    if (!baseUrl) {
      toast.error("VITE_APP_BASE_URL is not configured.");
      const fallbackQuestions = buildPlaceholderQuestions(totalRequested);
      const fallbackPayload = {
        questions: fallbackQuestions,
        question_count: fallbackQuestions.length,
        timer: fallbackQuestions.length * 60,
        subject: selectedSubject,
        topic: selectedTopic,
      };
      openQuizModal({
        quizPayload: fallbackPayload,
        totalQuestions: fallbackPayload.question_count,
        timer: fallbackPayload.timer,
        goalDetails: { subject: selectedSubject, topic: selectedTopic },
      });
      return;
    }

    setIsGenerating(true);
    try {
      const requestBody = {
        subject: selectedSubject,
        chapter: selectedTopic,
        num_questions: totalRequested,
        question_type: normalizeQuestionType(questionType),
        level: "Intermediate",
        subType: "samplePaper",
      };

      console.log("SamplePaper | Sending quiz generation request", {
        url: `${baseUrl}/v2/quiz/generate`,
        payload: requestBody,
      });

      const response = await fetch(`${baseUrl}/v2/quiz/generate`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        redirect: "follow",
        body: JSON.stringify(requestBody),
      });

      const responseText = await response.text();

      console.log("SamplePaper | Received quiz generation response", {
        status: response.status,
        ok: response.ok,
        body: responseText,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
      }

      let parsedData = {};
      if (responseText) {
        try {
          parsedData = JSON.parse(responseText);
        } catch (parseError) {
          console.error("Failed to parse quiz generator response:", parseError);
          throw new Error(
            "Received invalid JSON from the quiz generator service."
          );
        }
      }

      const quizPayload =
        parsedData?.result && typeof parsedData.result === "object"
          ? parsedData.result
          : parsedData;
      const questionList = Array.isArray(quizPayload?.questions)
        ? quizPayload.questions
        : [];

      if (!questionList.length) {
        throw new Error("API did not return any questions.");
      }

      const totalQuestions =
        typeof quizPayload?.question_count === "number" &&
        Number.isFinite(quizPayload.question_count)
          ? Math.max(quizPayload.question_count, questionList.length)
          : questionList.length || totalRequested;

      const timerSeconds =
        typeof quizPayload?.timer === "number" &&
        Number.isFinite(quizPayload.timer) &&
        quizPayload.timer > 0
          ? quizPayload.timer
          : totalQuestions * 60;

      openQuizModal({
        quizPayload,
        totalQuestions,
        timer: timerSeconds,
        goalDetails: { subject: selectedSubject, topic: selectedTopic },
      });
    } catch (error) {
      console.error("Failed to generate sample paper:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to generate sample paper."
      );
      const fallbackQuestions = buildPlaceholderQuestions(totalRequested);
      const fallbackPayload = {
        questions: fallbackQuestions,
        question_count: fallbackQuestions.length,
        timer: fallbackQuestions.length * 60,
        subject: selectedSubject,
        topic: selectedTopic,
      };
      openQuizModal({
        quizPayload: fallbackPayload,
        totalQuestions: fallbackPayload.question_count,
        timer: fallbackPayload.timer,
        goalDetails: { subject: selectedSubject, topic: selectedTopic },
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const submitHandler = () => {
    const fallbackCount =
      Number.isFinite(Number(selectedQuestions)) &&
      Number(selectedQuestions) > 0
        ? Number(selectedQuestions)
        : numQuestions;
    const fallbackQuestions = buildPlaceholderQuestions(fallbackCount);
    openQuizModal({
      quizPayload: {
        questions: fallbackQuestions,
        question_count: fallbackQuestions.length,
        timer: fallbackQuestions.length * 60,
        subject: selectedSubject,
        topic: selectedTopic,
      },
      totalQuestions: fallbackQuestions.length,
      timer: fallbackQuestions.length * 60,
      goalDetails: { subject: selectedSubject, topic: selectedTopic },
    });
  };

  useEffect(() => {
    if (!isModalOpen) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isModalOpen]);

  const closePracticeGame = () => {
    setIsModalOpen(false);
    setRemainingTime(0);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    resetQuizState();
  };

  useEffect(() => {
    setSelectedQuestions(numQuestions);
  }, [numQuestions]);

  // If we're on the nested generated route, only render the outlet
  if (isOnGeneratedRoute) {
    return <Outlet />;
  }

  return (
    <div className="w-full mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10 font-sans">
      {/* <div className="flex justify-between items-start mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-wide">
          Test Yourself for Exam Preparation
        </h1>
        <button
          onClick={() => navigate("/practice/samplePapers/generated")}
          className="px-4 py-2 rounded-lg text-[#0D99FF] text-sm font-medium border border-[#0D99FF] bg-white hover:bg-[#0D99FF] hover:text-white transition-all duration-300 ease-in-out shadow-sm hover:shadow-md"
        >
          View Generated Papers
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SelectionDropdown
          label="Select a Subject"
          iconName="Mathematics"
          selectedValue={selectedSubject}
          onChange={(e) => handleSubjectChange(e.target.value)}
          options={subjectOptions}
          disabled={isSyllabusLoading}
        />

        <SelectionDropdown
          label="Select a Chapter"
          iconName="Algebra"
          selectedValue={selectedTopic}
          onChange={(e) => setSelectedTopic(e.target.value)}
          options={topicOptions}
          disabled={
            !selectedSubject ||
            selectedSubject === "default" ||
            isSyllabusLoading
          }
        />

        <div className="flex flex-col">
          <label className="block mb-3 text-gray-800 font-semibold text-sm">
            Maximum number of questions
          </label>
          <div className="flex gap-4">
            {questionCountOptions.map((num) => (
              <button
                key={num}
                onClick={() => setNumQuestions(num)}
                className={`w-14 h-14 rounded-xl flex items-center justify-center text-lg font-bold transition-all duration-200
                  ${
                    numQuestions === num
                      ? "bg-[#0D99FF] text-white shadow-md shadow-[#0D99FF]/40 transform scale-105"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          <label className="block mb-2 text-gray-800 font-semibold text-sm">
            Select a Question Type
          </label>
          <div
            className={`relative flex items-center bg-white p-3 pr-4 rounded-xl cursor-pointer transition duration-300 ease-in-out
              ${
                questionType
                  ? "border-[#0D99FF] ring-2 ring-[#0D99FF]/20 shadow-lg"
                  : "border border-gray-300 hover:border-[#0D99FF]"
              }`}
          >
            <select
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value)}
              className="appearance-none bg-transparent border-none outline-none text-base text-gray-700 font-medium flex-grow pr-8 cursor-pointer"
            >
              {questionTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 w-5 h-5 text-gray-500 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-10">
        <button
          onClick={handleGenerate}
          disabled={
            isGenerating ||
            !selectedSubject ||
            selectedSubject === "default" ||
            !selectedTopic ||
            selectedTopic === "default" ||
            !questionType
          }
          className={`w-full sm:w-auto py-4 px-6 rounded-3xl text-white text-lg font-bold transition-all duration-300 ease-in-out
            ${
              isGenerating ||
              !selectedSubject ||
              selectedSubject === "default" ||
              !selectedTopic ||
              selectedTopic === "default" ||
              !questionType
                ? "bg-[#0D99FF]/60 cursor-not-allowed opacity-80"
                : "bg-gradient-to-r from-[#0D99FF] to-[#A08CFF] shadow-xl shadow-[#0D99FF]/40 hover:shadow-2xl hover:shadow-[#0D99FF]/60 hover:scale-[1.01]"
            }`}
        >
          {isGenerating ? "Generating..." : "Generate Sample Papers"}
        </button>
      </div> */}

      <div className="my-12">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#101828]">
            Sample Papers
          </h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl">
          {[
            { subject: "Mathematics", icon: Calculator },
            { subject: "Science", icon: FlaskConical },
          ].map((paper, idx) => {
            const IconComponent = paper.icon;
            return (
              <div
                key={`${paper.subject}-${idx}`}
                className="bg-gradient-to-r from-[#0D99FF] to-[#8B74F8] rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-5 flex flex-col transform hover:-translate-y-0.5 hover:scale-[1.01] hover:ring-1 hover:ring-white/30"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-white/20 rounded-xl p-3 flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3
                    className={`text-lg ${
                      paper.subject === "Mathematics"
                        ? "font-bold"
                        : "font-semibold"
                    } text-white`}
                  >
                    {paper.subject}
                  </h3>
                </div>

                <div className="mt-auto flex justify-center">
                  <button
                    onClick={() =>
                      navigate(
                        `/tutor?enableWorkbookmode=true&subject=${paper.subject}&tab=samplePaper&from=samplePaper`
                      )
                    }
                    className="w-3/4 px-7 py-3 rounded-full text-base font-semibold text-[#0D99FF] bg-white shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70 text-center"
                  >
                    View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Past Years Paper Section */}
      <div className="my-12">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#101828]">
            Past Years Paper
          </h1>
          <button
            onClick={() =>
              navigate(
                `/tutor?enableWorkbookmode=true&tab=pastYear&from=samplePaper`
              )
            }
            className="text-[#0D99FF] text-sm font-semibold hover:underline"
          >
            View more
          </button>
        </div>

        {/* 4 cards in a row on large screens, responsive down to 1-2 cols */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              subject: "Mathematics",
              questions: 38,
              year: 2025,
              time: "180 min",
            },
            { subject: "Science", questions: 39, year: 2025, time: "180 min" },
            {
              subject: "Mathematics",
              questions: 38,
              year: 2024,
              time: "180 min",
            },
            { subject: "Science", questions: 39, year: 2024, time: "180 min" },
          ].map((paper, idx) => (
            <div
              key={`${paper.subject}-${idx}`}
              className="bg-gradient-to-r from-[#0D99FF] to-[#8B74F8] rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-5 flex flex-col transform hover:-translate-y-0.5 hover:scale-[1.01] hover:ring-1 hover:ring-white/30"
            >
              {/* Header row: Subject + time badge */}
              <div className="flex items-center justify-between mb-3">
                <h3
                  className={`text-lg ${
                    paper.subject === "Mathematics"
                      ? "font-bold"
                      : "font-semibold"
                  } text-white`}
                >
                  {paper.subject}
                </h3>
                <span className="text-xs font-semibold text-white bg-white/20 rounded-full px-2.5 py-1">
                  {paper.time}
                </span>
              </div>

              {/* Meta row: Questions/year + download */}
              <div className="flex items-center justify-between text-sm text-white mb-6">
                <span>
                  <span className="block">
                    <span className="font-semibold bg-white/20 px-2 py-1 rounded-md">
                      Exam Year: {paper.year}
                    </span>
                  </span>
                  <span className="block">{paper.questions} Questions</span>
                </span>
                {/* <button
                  className="p-2 rounded-lg hover:bg-white/10 text-white transform transition-transform duration-200 hover:scale-105"
                  aria-label="Download"
                >
                  <Download className="w-5 h-5" />
                </button> */}
              </div>

              {/* Bottom Start button centered */}
              <div className="mt-auto flex justify-center">
                <button
                  onClick={() =>
                    navigate(
                      `/tutor?enableWorkbookmode=true&subject=${paper.subject}&tab=pastYear&from=samplePaper`
                    )
                  }
                  className="w-3/4 px-7 py-3 rounded-full text-base font-semibold text-[#0D99FF] bg-white shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70 text-center"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            aria-hidden="true"
          />
          <div className="relative w-[98vw] h-[98vh] max-w-none rounded-2xl bg-white p-0 shadow-xl overflow-hidden">
            <div className="h-full">
              <GoalQuestions embedded onClose={closePracticeGame} />
            </div>
          </div>
        </div>
      )}

      {/* Render nested routes */}
      <Outlet />
    </div>
  );
}

export default MathematicsSamplePaper;
